import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import APIService from "../services/APIService";
import { formatDateTime } from "../utils/DateTimeUtil";
import "./css/SimulationLanding.css";

const SimulationLanding = () => {
  const { shortCode } = useParams();
  const [searchParams] = useSearchParams();
  const [messageUuid, setMessageUuid] = useState("");
  const [originalUrl, setOriginalUrl] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [clickData, setClickData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const trackClick = async () => {
      // Validate shortCode format before making API call
      if (!shortCode || !shortCode.startsWith("e") || shortCode.length !== 19) {
        setError("Invalid link format");
        setLoading(false);
        return;
      }

      // Extract message UUID from short code (e.g., "eABC123XYZ456789012" -> "ABC123XYZ456789012")
      const uuid = shortCode.substring(1);
      setMessageUuid(uuid);

      // Get the origUrl from the URL query parameters
      const origUrlParam = searchParams.get("origUrl");
      console.log(
        "Email simulation click detected for message UUID:",
        uuid,
        "origUrl:",
        origUrlParam,
      );

      // Call backend to validate UUID and log the click
      try {
        // Build endpoint with origUrl query parameter if present
        let endpoint = `/api/v1/simulation/email-click/${shortCode}`;
        if (origUrlParam) {
          endpoint += `?origUrl=${encodeURIComponent(origUrlParam)}`;
        }

        const data = await APIService.get(endpoint);

        console.log("Email click tracked successfully:", data);

        // Backend validated the UUID - it exists
        if (data && data.success !== false) {
          setClickData(data);
          setIsValid(true);
          // Use originalUrl from backend (extracted from message content)
          setOriginalUrl(data.originalUrl || "N/A");
          console.log("Original URL from backend:", data.originalUrl);
        } else {
          setError("This link is not valid");
        }
      } catch (error) {
        console.error("Error tracking email click:", error);

        // Handle specific error cases
        if (error.response?.status === 404) {
          setError("This link is not valid or has expired");
        } else if (error.response?.status === 400) {
          setError("Invalid link format");
        } else {
          setError("Unable to verify this link. Please try again later.");
        }
      } finally {
        setLoading(false);
        setCurrentTime(new Date());
      }
    };

    trackClick();
  }, [shortCode, searchParams]);

  // Navigate to a specific section in the main app via URL-based deep linking
  const navigateToApp = (contentName) => {
    // Map content names to URL slugs for deep linking
    const contentToSlug = {
      "Training Center": "training",
      "Alerts Intelligence Hub": "alerts-hub",
      Dashboard: "dashboard",
    };
    const slug = contentToSlug[contentName] || "dashboard";
    window.location.href = `/app/${slug}`;
  };

  // Use centralized DateTimeUtil for consistent time formatting
  const formatTime = (date) => {
    return formatDateTime(date);
  };

  // Show loading state while validating UUID
  if (loading) {
    return (
      <div className="simulation-landing">
        <div className="simulation-container">
          <div className="simulation-header">
            <div className="warning-icon">
              <img
                src="/KinShield.png"
                alt="KinShield Logo"
                className="simulation-logo"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
            <h1>KinShield™ Protection Alert</h1>
          </div>
          <div className="simulation-content">
            <div className="detection-info">
              <h2>Validating link...</h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if UUID is invalid
  if (error || !isValid) {
    return (
      <div className="simulation-landing">
        <div className="simulation-container">
          <div className="simulation-header">
            <div className="warning-icon">
              <img
                src="/KinShield.png"
                alt="KinShield Logo"
                className="simulation-logo"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
            <h1>KinShield™ Protection Alert</h1>
          </div>
          <div className="simulation-content">
            <div className="detection-info">
              <h2>⚠️ Invalid Link</h2>
            </div>
            <div className="what-happened">
              <h3>Unable to Verify This Link</h3>
              <p className="explanation">
                {error || "This link is not valid or has expired."}
              </p>
              <p className="explanation">
                If you believe this is an error, please contact your KinShield
                administrator.
              </p>
            </div>
            <div className="btn-group">
              <button
                className="btn btn-primary"
                onClick={() => (window.location.href = "/")}
              >
                Return to KinShield Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="simulation-landing">
      <div className="simulation-container">
        {/* Header Section */}
        <div className="simulation-header">
          <div className="warning-icon">
            <img
              src="/KinShield.png"
              alt="KinShield Logo"
              className="simulation-logo"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>
          <h1>KinShield™ Protection Alert</h1>
          <div className="alert-banner">
            <strong>YOU ARE SAFE!</strong>
          </div>
        </div>

        {/* Main Content */}
        <div className="simulation-content">
          <div className="detection-info">
            <h2>
              🚨 This was a safe email phishing simulation for training
              purposes.
            </h2>
          </div>

          {/* What Happened Section */}
          <div className="what-happened">
            <h3>💡 What Just Happened?</h3>
            <p className="explanation">
              You just clicked on a link in an email—and in a real attack, that
              could have had serious consequences. But don't worry! This was a{" "}
              <strong>safe training exercise</strong> designed to help you
              recognize email phishing before it happens to you for real.
            </p>
            {originalUrl && originalUrl !== "N/A" && (
              <div className="url-reveal">
                <p className="url-label">The link you clicked:</p>
                <strong className="url-display">{originalUrl}</strong>
              </div>
            )}
          </div>

          {/* Why This Matters */}
          <div className="why-matters">
            <h3>🎯 Why This Matters</h3>
            <p>
              If this had been a real attack instead of a training simulation,
              clicking that link could have:
            </p>
            <ul>
              <li>Stolen your login credentials through a fake login page</li>
              <li>Installed malware or ransomware on your computer</li>
              <li>Captured your credit card numbers or banking information</li>
              <li>
                Compromised your entire organization's network through your
                account
              </li>
            </ul>
            <p className="reassurance">
              <strong>The good news:</strong> You're now more aware, and
              awareness is your best defense!
            </p>
          </div>

          {/* Educational Section */}
          <div className="micro-lesson">
            <h3>🎓 How to Spot Email Scams</h3>

            <div className="lesson-content">
              <div className="warning-signs">
                <h4>🚩 Red Flags to Watch For:</h4>
                <ul>
                  <li>
                    <strong>Creates urgency:</strong> "Your account will be
                    suspended!" or "Immediate action required!"
                  </li>
                  <li>
                    <strong>Unexpected requests:</strong> Surprise emails from
                    banks, IT departments, or colleagues asking for unusual
                    actions
                  </li>
                  <li>
                    <strong>Generic greetings:</strong> "Dear Customer" or "Dear
                    User" instead of your actual name
                  </li>
                  <li>
                    <strong>Suspicious sender addresses:</strong> Email
                    addresses that look almost right but have slight variations
                    (support@amaz0n.com instead of amazon.com)
                  </li>
                  <li>
                    <strong>Poor grammar/spelling:</strong> Professional
                    organizations rarely send emails with typos or awkward
                    phrasing
                  </li>
                  <li>
                    <strong>Mismatched URLs:</strong> Links that claim to go to
                    one site but actually point somewhere else (hover to check!)
                  </li>
                </ul>
              </div>

              <div className="safety-tips">
                <h4>✅ What to Do Instead:</h4>
                <ul>
                  <li>
                    <strong>Pause before clicking:</strong> Take a moment to
                    evaluate unexpected emails, especially with links or
                    attachments
                  </li>
                  <li>
                    <strong>Hover over links:</strong> Before clicking, hover
                    your mouse over any link to see where it really goes
                  </li>
                  <li>
                    <strong>Verify independently:</strong> Phishing emails can
                    look remarkably realeven if you're expecting a package or
                    recently contacted a company, that email could still be
                    fake. Always contact the sender directly using their
                    official website or phone number, not links or contact info
                    in the email.
                  </li>
                  <li>
                    <strong>Check the sender carefully:</strong> Look at the
                    full email address, not just the display name
                  </li>
                  <li>
                    <strong>Be wary of attachments:</strong> Don't open
                    unexpected attachments, even from known contacts
                  </li>
                  <li>
                    <strong>Report suspicious emails:</strong> Forward phishing
                    attempts to your IT security team or use your email client's
                    report feature
                  </li>
                  <li>
                    <strong>Trust your instincts:</strong> If something feels
                    off, it probably is
                  </li>
                </ul>
              </div>

              <div className="device-protection">
                <h4>🛡️ Additional Protection Tips:</h4>
                <ul>
                  <li>
                    Keep your operating system and software updated with the
                    latest security patches
                  </li>
                  <li>Use strong, unique passwords for each account</li>
                  <li>
                    Enable multi-factor authentication (2FA) wherever possible
                  </li>
                  <li>Install reputable antivirus and anti-malware software</li>
                  <li>
                    Be extra cautious with work emails that request money
                    transfers or sensitive data
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Encouragement Section */}
          <div className="encouragement">
            <h3>💪 You're Building Better Habits</h3>
            <p>
              Every training exercise like this one strengthens your ability to
              recognize threats. The fact that you're seeing this page means
              you're actively learning and improving your security awareness.
              Next time you receive a suspicious email, you'll be more prepared
              to spot the warning signs and protect yourself.
            </p>
            <p className="final-tip">
              <strong>Remember:</strong> When in doubt, don't click. Verify
              first, click later or not at all.
            </p>
          </div>

          {/* Next Steps */}
          <div className="next-steps">
            <h3>📚 What's Next?</h3>
            <p>
              Take your awareness to the next level with targeted training and
              real-time security insights.
            </p>
            <div className="btn-group">
              <button
                className="btn btn-primary"
                onClick={() => navigateToApp("Training Center")}
              >
                Start Phishing Training
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigateToApp("Alerts Intelligence Hub")}
              >
                View Security Alerts
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => navigateToApp("Dashboard")}
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="simulation-footer">
          <p>
            This email simulation is part of KinShield's security awareness
            training program. Regular training helps build stronger security
            habits and protects against real threats via email.
          </p>
          <div className="timestamp">
            Simulation logged at: {formatTime(currentTime)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationLanding;
